import { NgFor, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, take } from 'rxjs';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { TableComponent } from '../../../core/table/table.component';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { FilterDataType } from '../../../models/core/table/FilterDataType';
import { QueryBuilderParams } from '../../../models/core/table/QueryBuilderParams';
import { SearchExpression } from '../../../models/core/table/SearchExpression';
import { TableActionButton } from '../../../models/core/table/TableActionButton';
import { TableColumn } from '../../../models/core/table/TableColumn';
import { TableEvent } from '../../../models/core/table/TableEvent';
import { TableSearchEventData } from '../../../models/core/table/TableSearchEventData';
import { OrderList } from '../../../models/domain/Order';
import { EventTypes } from '../../../models/enums/EventTypes';
import { GroupType } from '../../../models/enums/GroupType';
import {
    OrderStatus,
    OrderStatusLabels,
} from '../../../models/enums/OrderStatus';
import { LoadingService } from '../../../services/loading.service';
import { OrderService } from '../../../services/order.service';
import { getSearchOperator } from '../../../utilities/common-functions';
import { OrderDetailComponent } from '../order-detail/order-detail.component';

interface StatusFilterItem {
    value: OrderStatus;
    label: string;
    cssKey: string;
}

@Component({
    selector: 'app-order-list',
    imports: [RouterLink, TableComponent, OrderDetailComponent, NgIf, NgFor],
    templateUrl: './order-list.component.html',
    styleUrl: './order-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderListComponent implements OnInit {

    //#region Table Variables
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    selectedOrderId = 0;
    classApplied = false;
    isToggled = false;
    activeStatusFilter: OrderStatus | null = null;

    readonly allStatuses: StatusFilterItem[] = [
        { value: OrderStatus.OrderPlaced,  label: 'Order Placed',  cssKey: 'placed'     },
        { value: OrderStatus.Accepted,     label: 'Accepted',      cssKey: 'accepted'   },
        { value: OrderStatus.OnHold,       label: 'On Hold',       cssKey: 'hold'       },
        { value: OrderStatus.InProduction, label: 'In Production', cssKey: 'production' },
        { value: OrderStatus.Shipped,      label: 'Shipped',       cssKey: 'shipped'    },
        { value: OrderStatus.Completed,    label: 'Completed',     cssKey: 'completed'  },
        { value: OrderStatus.Rejected,     label: 'Rejected',      cssKey: 'rejected'   },
        { value: OrderStatus.Cancelled,    label: 'Cancelled',     cssKey: 'cancelled'  },
    ];

    constructor(
        public themeService: CustomizerSettingsService,
        public orderService: OrderService,
        private readonly toasterService: ToastrService,
        private readonly loadingService: LoadingService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.getData();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
        this.searchOptions = new BaseSearchOptions(
            undefined,
            'createdOn',
            1,
            this.pageIndex,
            this.pageSize,
        );
        this.buildTable();
    }

    private getData(): void {
        this.loadingService.showLoading();
        this.orderService
            .getAllOrders(this.searchOptions)
            .pipe(
                take(1),
                catchError((error) => {
                    const msg = error?.error?.isCustom
                        ? error.error.message
                        : 'Something went wrong!';
                    this.toasterService.error(msg);
                    return of(null);
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                    this.cdr.markForCheck();
                }),
            )
            .subscribe((result) => {
                if (result) {
                    this.totalRecords = result.body?.totalCount ?? 0;
                    this.orderService.dataSubjectUpdate(
                        result.body?.values ?? [],
                    );
                }
            });
    }

    public buildTable(): void {
        this.columns = [
            new TableColumn(
                'orderNo',
                'Order No',
                FilterDataType.String,
                true,
                false,
                (row: OrderList) => `#${row.orderNo}`,
                true,
                { tooltip: 'Order No' },
            ),
            new TableColumn(
                'tenantName',
                'Client (B2C)',
                FilterDataType.String,
                true,
                false,
                (row: OrderList) => row.tenantName ?? '—',
                true,
                { tooltip: 'B2C Tenant' },
            ),
            new TableColumn(
                'userEmail',
                'Ordered By',
                FilterDataType.String,
                true,
                false,
                (row: OrderList) => row.userEmail ?? '—',
                true,
                { tooltip: 'User Email' },
            ),
            new TableColumn(
                'totalQty',
                'Qty',
                FilterDataType.Int,
                true,
                false,
                (row: OrderList) => `${row.totalQty ?? 0}`,
                true,
                { tooltip: 'Total Quantity' },
            ),
            new TableColumn(
                'totalWeight',
                'Weight (kg)',
                FilterDataType.Decimal,
                true,
                false,
                (row: OrderList) => `${row.totalWeight ?? 0}`,
                true,
                { tooltip: 'Total Weight in kg' },
            ),
            new TableColumn(
                'orderStatus',
                'Status',
                FilterDataType.String,
                true,
                false,
                (row: OrderList) =>
                    OrderStatusLabels[row.orderStatus as OrderStatus] ??
                    row.orderStatusLabel,
                true,
                { tooltip: 'Order Status' },
            ),
            new TableColumn(
                'createdOn',
                'Ordered On',
                FilterDataType.DateTime,
                true,
                false,
                (row: OrderList) =>
                    row.createdOn
                        ? new Date(row.createdOn).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                          })
                        : '—',
                true,
                { tooltip: 'Order Date' },
            ),
        ];

        this.actions = [
            new TableActionButton(
                'visibility',
                (event, value) => this.onViewClick(event, value as OrderList),
                () => false,
                'View Details',
                'View Details',
            ),
        ];
    }

    public filterByStatus(status: OrderStatus | null): void {
        this.activeStatusFilter = status;
        if (!status) {
            this.searchOptions.filter = '';
        } else {
            this.searchOptions.filter = this.searchOptions.generateQuery([{
                fieldName: 'orderStatus',
                dataType: FilterDataType.String,
                searchOperator: SearchExpression.Equals,
                searchValue: status,
                groupType: GroupType.AND,
            }]);
        }
        this.searchOptions.pageIndex = 0;
        this.getData();
    }

    public onViewClick(event: MouseEvent, order: OrderList): void {
        event.stopPropagation();
        this.selectedOrderId = order.orderId;
        this.classApplied = true;
        this.cdr.markForCheck();
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;
            this.searchOptions.updateByObject(eventValue.baseSearchOptions);
            if (eventValue.columnFilterValues) {
                this.createFilter(eventValue);
                this.activeStatusFilter = null;
            } else {
                this.searchOptions.filter = '';
                this.activeStatusFilter = null;
            }
            this.getData();
        }
        if (event.type === EventTypes.CLEAR) {
            this.clearFilters();
            this.getData();
        }
    }

    public clearFilters(): void {
        this.activeStatusFilter = null;
        this.pageIndex = 0;
        this.pageSize = 10;
        this.searchOptions = new BaseSearchOptions(
            undefined,
            'createdOn',
            1,
            this.pageIndex,
            this.pageSize,
        );
    }

    private createFilter(eventValue: TableSearchEventData): void {
        const queryParams: QueryBuilderParams[] = [];
        for (const [columnName, { dataType, opType, searchValue }]
            of Object.entries(eventValue.columnFilterValues)) {
            const searchOperator = getSearchOperator(opType as unknown as string);
            if (!searchOperator) continue;
            queryParams.push({
                fieldName: columnName,
                dataType,
                searchOperator,
                searchValue,
                groupType: GroupType.OR,
            });
        }
        this.searchOptions.filter = this.searchOptions.generateQuery(queryParams);
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
        this.cdr.markForCheck();
    }

    public refreshList(): void {
        this.getData();
    }
}
